import * as React from 'react';
import { useLocation } from 'react-router';
import type { PiletApi } from '@chess/platform-web';
import { canonicalFor, robotsFor, titleFor } from './titles.ts';

// The document's head, kept current per route (2026-10-20). The app is one
// HTML file on every address, so what distinguishes the routes for a
// crawler, a tab strip or a bookmark is set here on every pathname change:
// the title ("<Screen> · <brand>"; the landing page "<brand> · <tagline>"),
// one `<meta name="robots">` ("index,follow" on `/` and `/welcome`, the
// public landing; "noindex,follow" on every other route, which is the
// reader's private data), and the canonical link, present on the indexable
// routes only. The static head in index.html carries the description, the
// Open Graph and Twitter cards, the icons and the manifest; they do not
// change per route. `titles.ts` holds the rules, pure and tested; this file
// only writes the DOM. A component in `shell.gate`, drawing nothing.

const ROBOTS = 'meta[name="robots"]';
const CANONICAL = 'link[rel="canonical"]';

/** The site's origin for the canonical link: the static link's (production's), else this page's. */
function canonicalBase(): string {
  const href = document.head.querySelector<HTMLLinkElement>(CANONICAL)?.getAttribute('href');
  if (href) {
    try {
      return new URL(href).origin;
    } catch {
      // fall through to the page's own origin
    }
  }
  return location.origin;
}

function setRobots(content: string): void {
  let meta = document.head.querySelector<HTMLMetaElement>(ROBOTS);
  if (!meta) {
    meta = document.createElement('meta');
    meta.name = 'robots';
    document.head.append(meta);
  }
  if (meta.content !== content) meta.content = content;
}

function setCanonical(href: string | undefined): void {
  let link = document.head.querySelector<HTMLLinkElement>(CANONICAL);
  if (!href) {
    link?.remove();
    return;
  }
  if (!link) {
    link = document.createElement('link');
    link.rel = 'canonical';
    document.head.append(link);
  }
  if (link.getAttribute('href') !== href) link.setAttribute('href', href);
}

export function setup(app: PiletApi) {
  const brand = app.tryGetApi('brand');
  // Read once, before any route removes the static link.
  const base = canonicalBase();

  const Head: React.FC = () => {
    const { pathname } = useLocation();
    React.useEffect(() => {
      document.title = titleFor(pathname, brand);
      setRobots(robotsFor(pathname));
      setCanonical(canonicalFor(pathname, base));
    }, [pathname]);
    return null;
  };
  app.registerExtension('shell.gate', Head);
}
